import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { deleteAnsweringMachineCallSession, deleteAnsweringMachineCallSessionBulk, getAnsweringMachine, getAnsweringMachineCallSession, getAnsweringMachineCallSessions, getAnsweringMachineCallSessionsCount, getAnsweringMachineCallSessionsIds, getAnsweringMachinePhoneNumber, getAnsweringMachinePhoneNumberOrderRequirements, patchAnsweringMachine, patchAnsweringMachineCallSession, patchAnsweringMachineCallSessionBulk, patchAnsweringMachinePhoneNumberOrderRequirements, postAnsweringMachinePhoneNumberOrder } from "./requests";
import { notifyError, notifySuccess } from "@/toast-notifications";
import useFetchQuery from "@/hooks/useFetchQuery";


export const ANSWERING_MACHINE_KEY = "answering-machine";

/** @param {object} [options] Extra react-query options. */
export function useGetAnsweringMachine(options = {}) {
    const t = useTranslation();
    return useQuery({
        queryKey: [ANSWERING_MACHINE_KEY],
        queryFn: getAnsweringMachine,
        onError: () => {
            notifyError(t("answering_machine:queries.answering_machine.get_error"));
        },
        ...options,
    });
}

/** @param {object} [options] Extra react-query options. */
export function usePatchAnsweringMachine(options = {}) {
    const t = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: patchAnsweringMachine,
        onMutate: (variables) => {
            // Optimistic update
            queryClient.setQueryData(
                [ANSWERING_MACHINE_KEY,],
                (previousState) => ({ ...previousState, ...variables }),
            );
        },
        onSuccess: (data) => {
            // Updating with server data
            queryClient.setQueryData([ANSWERING_MACHINE_KEY,], data);
            notifySuccess(t("answering_machine:queries.answering_machine.patch_success"));
        },
        onError: () => {
            // Invalidating optimistic update
            queryClient.invalidateQueries({
                queryKey: [ANSWERING_MACHINE_KEY,],
            });
            notifyError(t("answering_machine:queries.answering_machine.patch_error"));
        },
        ...options,
    });
}

export const CALL_SESSIONS_KEY = "call-sessions";

/**
 * @param {object} params Request params.
 * @param {object} [options] Extra react-query options.
 */
export function useGetAnsweringMachineCallSessions(params, options = {}) {
    const t = useTranslation();
    return useQuery({
        queryKey: [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY, params],
        queryFn: () => getAnsweringMachineCallSessions(params),
        onError: () => {
            notifyError(t("answering_machine:queries.call_sessions.get_error"));
        },
        ...options,
    });
}

export function useGetAnsweringMachineCallSession(id, options = {}) {
    const t = useTranslation();
    return useQuery({
        queryKey: [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY, id],
        queryFn: () => getAnsweringMachineCallSession(id),
        onError: () => {
            notifyError(t("answering_machine:queries.call_sessions.get_error"));
        },
        ...options,
    });
}

export const CALL_SESSIONS_COUNT_KEY = "count";

/**
 * @param {object} params Request params.
 * @param {object} [options] Extra react-query options.
 */
export function useGetAnsweringMachineCallSessionsCount(params, options = {}) {
    const t = useTranslation();
    return useQuery({
        queryKey: [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY, CALL_SESSIONS_COUNT_KEY, params],
        queryFn: () => getAnsweringMachineCallSessionsCount(params),
        onError: () => {
            notifyError(t("answering_machine:queries.call_sessions.count_error"));
        },
        ...options,
    });
}

export const CALL_SESSIONS_IDS_KEY = "ids";

export function useGetAnsweringMachineCallSessionsIds(params, options = {}) {
    const t = useTranslation();
    return useQuery({
        queryKey: [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY, CALL_SESSIONS_IDS_KEY, params],
        queryFn: () => getAnsweringMachineCallSessionsIds(params),
        onError: () => {
            notifyError(t("answering_machine:queries.call_sessions.get_error"));
        },
        ...options,
    });
}

export function useFetchGetAnsweringMachineCallSessionsIds(options = {}) {
    return useFetchQuery(
        (_, args) => getAnsweringMachineCallSessionsIds(...args),
        (params) => [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY, CALL_SESSIONS_IDS_KEY, params],
        options
    );
}

/** @param {object} [options] Extra react-query options. */
export function usePatchAnsweringMachineCallSession(options = {}) {
    const t = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: patchAnsweringMachineCallSession,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY,]
            });
            // notifySuccess(t("call_sessions.patch_success"));
        },
        onError: () => {
            notifyError(t("answering_machine:queries.call_sessions.patch_error"));
        },
        ...options,
    });
}

/** @param {object} [options] Extra react-query options. */
export function useDeleteAnsweringMachineCallSession(options = {}) {
    const t = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteAnsweringMachineCallSession,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY,],
            });
            notifySuccess(t("answering_machine:queries.call_sessions.delete_success"));
        },
        onError: () => {
            notifyError(t("call_sessions.delete_error"));
        },
        ...options,
    });
}

export function usePatchAnsweringMachineCallSessionBulk(options = {}) {
    const queryClient = useQueryClient();
    const t = useTranslation();
    return useMutation({
        mutationFn: patchAnsweringMachineCallSessionBulk,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY,],
            });
        },
        onError: () => {
            notifyError(t("answering_machine:queries.call_sessions.patch_error"));
        },
        ...options,
    })
}

export function useDeleteAnsweringMachineCallSessionBulk(options = {}) {
    const queryClient = useQueryClient();
    const t = useTranslation();
    return useMutation({
        mutationFn: deleteAnsweringMachineCallSessionBulk,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ANSWERING_MACHINE_KEY, CALL_SESSIONS_KEY,],
            });
        },
        onError: () => {
            notifyError(t("answering_machine:queries.call_sessions.delete_error"));
        },
        ...options,
    })
}

export const PHONE_NUMBER_KEY = "phone-number";

/** @param {object} [options] Extra react-query options. */
export function useGetAnsweringMachinePhoneNumber(options = {}) {
    const t = useTranslation();
    return useQuery({
        queryKey: [ANSWERING_MACHINE_KEY, PHONE_NUMBER_KEY],
        queryFn: getAnsweringMachinePhoneNumber,
        onError: () => {
            notifyError(t("answering_machine:queries.phone_number.get_error"));
        },
        ...options,
    });
}

export const PHONE_NUMBER_ORDER_REQUIREMENTS_KEY = "phone-number-order-requirements";

/** @param {object} [options] Extra react-query options. */
export function useGetAnsweringMachinePhonenumberOrderRequirements(options = {}) {
    const t = useTranslation();
    return useQuery({
        queryKey: [ANSWERING_MACHINE_KEY, PHONE_NUMBER_KEY, PHONE_NUMBER_ORDER_REQUIREMENTS_KEY,],
        queryFn: getAnsweringMachinePhoneNumberOrderRequirements,
        onError: () => {
            notifyError(t("answering_machine:queries.phone_number_order_requirements.get_error"));
        },
        ...options,
    });
}

/** @param {object} [options] Extra react-query options. */
export function usePatchAnsweringMachinePhonenumberOrderRequirements(options = {}) {
    const t = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: patchAnsweringMachinePhoneNumberOrderRequirements,
        onSuccess: () => {
            notifySuccess(t("answering_machine:queries.phone_number_order_requirements.patch_success"));
            queryClient.invalidateQueries({
                queryKey: [ANSWERING_MACHINE_KEY, PHONE_NUMBER_KEY, PHONE_NUMBER_ORDER_REQUIREMENTS_KEY],
            });
        },
        onError: () => {
            notifyError(t("answering_machine:queries.phone_number_order_requirements.patch_error"));
        },
        ...options,
    });
}

/** @param {object} [options] Extra react-query options. */
export function usePostAnsweringMachinePhoneNumberOrder(options = {}) {
    const t = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: postAnsweringMachinePhoneNumberOrder,
        onSuccess: () => {
            notifySuccess(t("answering_machine:queries.phone_number_order.post_success"));
            queryClient.invalidateQueries({
                queryKey: [ANSWERING_MACHINE_KEY, PHONE_NUMBER_KEY,],
            });
        },
        onError: () => {
            notifyError(t("phone_number_order.post_error"));
        },
        ...options,
    });
}


// WEBPACK FOOTER //
// ./src/features/answering-machine/queries/hooks.js